import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subject } from 'rxjs'
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, switchMap, takeUntil, tap } from 'rxjs/operators'
import { of } from 'rxjs'
import { LeaderService } from '../../services/leader.service'

/**
 * SearchItem shape mirrors LTK.Model/SearchResponseDto.cs → SearchItemDto.
 * Serialized as camelCase on the wire.
 */
interface SearchItem {
   id: number
   title: string
   snippet: string
   module: string // 'task' | 'goal' | 'meeting' (per usp_GlobalSearch)
   date?: string
   url?: string
}

interface SearchResultsBuckets {
   tasks: SearchItem[]
   goals: SearchItem[]
   meetings: SearchItem[]
   hospitals: SearchItem[]
}

type GroupFilter = 'all' | 'goals' | 'tasks' | 'meetings' | 'hospitals'

const PAGE_SIZE = 10
const EMPTY_RESULTS: SearchResultsBuckets = { tasks: [], goals: [], meetings: [], hospitals: [] }

/**
 * Debounce window for URL-driven fetches. Mainly protects against rapid
 * paginator clicks and any other tight URL change loops. Typing in the
 * header bar is already debounced upstream (HEADER_SEARCH_DEBOUNCE_MS in
 * header-row.component.ts), so this only adds a small extra cushion.
 */
const SEARCH_URL_DEBOUNCE_MS = 250

/**
 * Minimum characters before we hit the API. Mirrors the same guard in the
 * header bar so direct URL hits (e.g. someone pasting /leader-toolkit-search?q=a)
 * don't fire a noisy 1-char search either.
 */
const MIN_SEARCH_CHARS = 2

@Component({
   selector: 'leader-toolkit-search-results',
   templateUrl: './search-results-page.component.html',
   styleUrls: ['./search-results-page.component.scss'],
   standalone: false,
})
export class SearchResultsPageComponent implements OnInit, OnDestroy {
   @HostBinding('class.full-flex-height') fullFlexHeight = true

   // URL-bound state
   query = ''
   page = 1
   filter: GroupFilter = 'all'

   // Server state
   totalResults = 0
   results: SearchResultsBuckets = { ...EMPTY_RESULTS }
   isLoading = false
   hasError = false

   readonly pageSize = PAGE_SIZE
   readonly minSearchChars = MIN_SEARCH_CHARS
   readonly filterTabs: { key: GroupFilter; label: string }[] = [
      { key: 'all', label: 'All' },
      { key: 'hospitals', label: 'Hospitals' },
      { key: 'goals', label: 'Goals' },
      { key: 'tasks', label: 'Tasks' },
      { key: 'meetings', label: 'Meetings' },
   ]

   private readonly destroy$ = new Subject<void>()

   constructor(
      private route: ActivatedRoute,
      private router: Router,
      private leaderService: LeaderService
   ) {}

   ngOnInit(): void {
      // If the header's "View All Results" hand-off passed its already-
      // fetched page-1 payload via NavigationExtras.state, paint with it
      // immediately AND remember to skip the matching first fetch. Strip
      // the marker out of history.state straight away so a hard refresh
      // doesn't keep re-using stale cached data.
      let pendingInitial: { q: string; page: number } | null = null
      try {
         const navState: any = (typeof history !== 'undefined' && history.state) || {}
         const cached = navState.initialSearchResults
         if (cached?.results) {
            this.totalResults = Number(cached.totalResults) || 0
            this.results = {
               tasks: Array.isArray(cached.results.tasks) ? cached.results.tasks : [],
               goals: Array.isArray(cached.results.goals) ? cached.results.goals : [],
               meetings: Array.isArray(cached.results.meetings) ? cached.results.meetings : [],
               hospitals: Array.isArray(cached.results.hospitals) ? cached.results.hospitals : [],
            }
            pendingInitial = {
               q: (cached.query || '').toString().trim(),
               page: 1, // header dropdown only ever fetches page 1
            }
            const cleaned = { ...navState }
            delete cleaned.initialSearchResults
            history.replaceState(cleaned, '')
         }
      } catch { /* non-browser env — ignore */ }

      // Single pipeline rooted at queryParamMap. Previously we fed an
      // intermediate fetch$ Subject from inside the queryParamMap subscriber,
      // which created a subscription-order race: on browser-back to the same
      // URL, queryParamMap emits synchronously from its cached value, and the
      // fetch$ subscription wasn't yet attached when .next() ran -> emission
      // was dropped, no HTTP call, page rendered "no results" even though
      // the q was present in the URL. Folding everything into one chain
      // removes that bug class entirely.
      this.route.queryParamMap
         .pipe(
            takeUntil(this.destroy$),
            // Mirror the URL into component state on EVERY emission so the
            // filter tabs stay in sync, even when we end up not refetching
            // (e.g. only `type` changed -> FE-only filter).
            tap((params) => {
               const q = (params.get('q') || '').trim()
               const pageParam = Number(params.get('page') || '1')
               const typeParam = (params.get('type') || 'all') as GroupFilter

               this.query = q
               this.page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1
               this.filter = this.isValidFilter(typeParam) ? typeParam : 'all'

               // Below the min-char threshold (including empty) => clear
               // previously-fetched results so the user doesn't see stale
               // data while their query is too short to fetch.
               if (q.length < MIN_SEARCH_CHARS) {
                  this.results = { ...EMPTY_RESULTS }
                  this.totalResults = 0
                  this.hasError = false
                  this.isLoading = false
               }
            }),
            // Only proceed to fetch when there is a non-empty query that
            // meets the min-char threshold. Protects against direct URL
            // hits like ?q=a (one character) — would otherwise trigger an
            // expensive FREETEXT search on a near-universal match.
            filter((params) => (params.get('q') || '').trim().length >= MIN_SEARCH_CHARS),
            // Skip the first emission ONLY when it matches the pre-loaded
            // cache from the header (same q, same page=1). Any other
            // emission — page change, query change, browser-back without
            // cache — flows through untouched.
            filter((params) => {
               if (!pendingInitial) return true
               const q = (params.get('q') || '').trim()
               const page = Number(params.get('page') || '1') || 1
               if (q === pendingInitial.q && page === pendingInitial.page) {
                  pendingInitial = null
                  return false
               }
               pendingInitial = null
               return true
            }),
            // Collapse rapid URL changes (e.g. multiple paginator clicks).
            debounceTime(SEARCH_URL_DEBOUNCE_MS),
            // Refetch only when q or page actually changed. Filter-tab clicks
            // change only `type`, so they correctly do NOT refetch.
            distinctUntilChanged((a, b) => (a.get('q') || '') === (b.get('q') || '') && (a.get('page') || '1') === (b.get('page') || '1')),
            tap(() => {
               this.isLoading = true
               this.hasError = false
            }),
            switchMap((params) => {
               const q = (params.get('q') || '').trim()
               const page = Number(params.get('page') || '1') || 1
               return this.leaderService.globalSearch(q, page).pipe(
                  catchError(() => {
                     this.hasError = true
                     return of(null)
                  }),
                  finalize(() => (this.isLoading = false))
               )
            })
         )
         .subscribe((res: any) => {
            if (!res || !res.data) {
               this.results = { ...EMPTY_RESULTS }
               this.totalResults = 0
               return
            }
            const data = res.data
            this.totalResults = Number(data.totalResults) || 0
            this.results = {
               tasks: Array.isArray(data?.results?.tasks) ? data.results.tasks : [],
               goals: Array.isArray(data?.results?.goals) ? data.results.goals : [],
               meetings: Array.isArray(data?.results?.meetings) ? data.results.meetings : [],
               hospitals: Array.isArray(data?.results?.hospitals) ? data.results.hospitals : [],
            }
         })
   }

   ngOnDestroy(): void {
      this.destroy$.next()
      this.destroy$.complete()
   }

   // -------- Helpers exposed to template --------

   isFilterVisible(group: 'goals' | 'tasks' | 'meetings' | 'hospitals'): boolean {
      return this.filter === 'all' || this.filter === group
   }

   get visibleTotal(): number {
      return this.totalResults
   }

   trackById = (_: number, item: SearchItem) => item.id

   // -------- User actions --------

   /**
    * Retry button shown in the error state. Re-issues the same query/page
    * the URL currently represents (the URL itself doesn't change, so the
    * queryParamMap-driven pipeline wouldn't re-fire on its own).
    */
   retry(): void {
      const q = (this.query || '').trim()
      if (!q) return
      this.isLoading = true
      this.hasError = false
      this.leaderService
         .globalSearch(q, this.page)
         .pipe(
            takeUntil(this.destroy$),
            catchError(() => {
               this.hasError = true
               return of(null)
            }),
            finalize(() => (this.isLoading = false))
         )
         .subscribe((res: any) => {
            if (!res || !res.data) {
               this.results = { ...EMPTY_RESULTS }
               this.totalResults = 0
               return
            }
            const data = res.data
            this.totalResults = Number(data.totalResults) || 0
            this.results = {
               tasks: Array.isArray(data?.results?.tasks) ? data.results.tasks : [],
               goals: Array.isArray(data?.results?.goals) ? data.results.goals : [],
               meetings: Array.isArray(data?.results?.meetings) ? data.results.meetings : [],
               hospitals: Array.isArray(data?.results?.hospitals) ? data.results.hospitals : [],
            }
         })
   }

   onFilterChange(type: GroupFilter): void {
      if (this.filter === type) return
      // Pure FE filter — no fetch. Reset page=1 when filter changes
      // so the paginator label stays consistent.
      this.updateUrl({ q: this.query, page: 1, type })
   }

   onPageChange(nextPage: number): void {
      const safe = Math.max(1, nextPage | 0)
      if (safe === this.page) return
      this.updateUrl({ q: this.query, page: safe, type: this.filter })
   }

   onResultClick(item: SearchItem): void {
      if (!item) return
      const module = (item.module || '').toLowerCase()

      // IMPORTANT: prefer the id encoded in item.url over item.id.
      // The BE search SP (usp_GlobalSearch) sets item.id to:
      //   - TaskId     for matches in Tasks
      //   - CommentId  for matches in TaskComments   ← sub-entity, NOT a TaskId
      //   - MeetingId  for matches in ScheduleMeetings
      //   - NotesId    for matches in MeetingNotes   ← sub-entity, NOT a MeetingId
      //   - GoalId     for matches in GoalDictionary  (BE will be updated to
      //                return GoalTargetId so the popup can open directly)
      //   - HospitalId for matches in Hospital
      // But item.url is ALWAYS the parent entity route: /tasks/{TaskId},
      // /meetings/{MeetingId}, /goals/{GoalId}, /hospitals/{HospitalId}. So
      // extracting the trailing numeric segment of item.url gives us the
      // correct parent id in every case — comment/note matches now open the
      // right task/meeting popup.
      const urlId = this.extractIdFromUrl(item.url)
      const entityId = urlId && urlId > 0 ? urlId : item.id
      if (!entityId || entityId <= 0) return

      // We pass the highlight intent via NavigationExtras.state (in-memory)
      // rather than queryParams. This intentionally keeps the destination
      // URL clean — opening / closing the detail popup, or opening another
      // row from the same page, must not leave stale ?highlight=... query
      // params hanging around in the address bar.
      const extras = {
         state: { searchHighlight: { id: entityId } },
      }

      if (module === 'task') {
         this.router.navigate(['/leader-toolkit-tasks'], extras)
      } else if (module === 'goal') {
         this.router.navigate(['/leader-toolkit-goals'], extras)
      } else if (module === 'meeting') {
         this.router.navigate(['/leader-toolkit-scheduling'], extras)
      } else if (module === 'hospital') {
         // Hospital routes don't use the search-highlight state pattern —
         // the destination ops-dashboard is keyed entirely by the URL
         // (`:hospitalid` path param). We deliberately omit ?quarter= here:
         // HospitalOpsDashboardComponent defaults `selectedQuarter` to
         // 'default' when the param is missing and its existing
         // getQtrList() handler resolves that to the current quarter —
         // no extra FE round-trip needed.
         // Note: the route segment is `leader-tookit-hospitals` (typo
         // preserved from the existing app route definition).
         this.router.navigate(['/leader-tookit-hospitals', entityId, 'ops-dashboard'])
      }
   }

   /**
    * Pull the trailing numeric segment out of a `/tasks/43`-style URL.
    * Returns null on a malformed/empty URL — caller must fall back to item.id.
    */
   private extractIdFromUrl(url: string | undefined | null): number | null {
      if (!url) return null
      const parts = url.split('/').filter((p) => p && p.length > 0)
      if (!parts.length) return null
      const last = parts[parts.length - 1]
      const n = Number(last)
      return Number.isFinite(n) && n > 0 ? n : null
   }

   // -------- Pagination math (single global paginator) --------

   get totalPages(): number {
      if (this.totalResults <= 0) return 0
      return Math.ceil(this.totalResults / this.pageSize)
   }

   get hasPrevious(): boolean {
      return this.page > 1
   }

   get hasNext(): boolean {
      return this.page < this.totalPages
   }

   prevPage(): void {
      if (this.hasPrevious) this.onPageChange(this.page - 1)
   }

   nextPage(): void {
      if (this.hasNext) this.onPageChange(this.page + 1)
   }

   // -------- Private --------

   private updateUrl(state: { q: string; page: number; type: GroupFilter }): void {
      this.router.navigate([], {
         relativeTo: this.route,
         queryParams: { q: state.q, page: state.page, type: state.type },
         queryParamsHandling: 'merge',
      })
   }

   private isValidFilter(value: string): value is GroupFilter {
      return (
         value === 'all' ||
         value === 'goals' ||
         value === 'tasks' ||
         value === 'meetings' ||
         value === 'hospitals'
      )
   }
}
